import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTutorial } from '@/contexts/TutorialContext';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

import type { FacilityEntity, FacilityEntityType, ParkingLotSpotRow, ShopBayRow } from '@/types/facility';
import { mapFacilityEntityToShopBayUpdate, ENTITY_RENDER_CONFIG } from '@/types/facility';
import { generateParkingRow, generateWizardLayout } from '@/lib/facilityLayoutPresets';
import { nextLabelNumber } from '@/lib/parkingLabels';
import {
  isStandaloneParkingSpot,
  mergeFacilityInventory,
  planStandaloneSpotUpdates,
  restrictStandaloneSpotUpdates,
} from '@/lib/facility/parkingInventory';

import { Viewport } from '@/components/facility/Viewport';
import { FacilityCanvas } from '@/components/facility/FacilityCanvas';
import { ToolPalette } from '@/components/facility/ToolPalette';
import { InspectorPanel } from '@/components/facility/InspectorPanel';
import { ParkingRowDialog } from '@/components/facility/ParkingRowDialog';
import { SetupWizard } from '@/components/facility/SetupWizard';
import { CameraManager } from '@/components/facility/cameras/CameraManager';
import { Coachmark } from '@/components/onboarding/Coachmark';
import { GRID_SIZE } from '@/components/facility/constants';
import type { Bounds } from '@/hooks/useViewportCamera';
import { layoutSaveRefusalMessage } from '@/lib/facility/layoutSaveRefusal';

// One grid space per arrow keypress. Holding the key relies on the OS/browser
// auto-repeat, which fires repeated `keydown` events — no custom timer.
const ARROW_KEY_STEP: Record<string, [number, number]> = {
  ArrowLeft: [-GRID_SIZE, 0],
  ArrowRight: [GRID_SIZE, 0],
  ArrowUp: [0, -GRID_SIZE],
  ArrowDown: [0, GRID_SIZE],
};

// Widget roles whose arrow keys already mean something (Radix selects, tabs,
// sliders, menus, ...). Arrow-key object movement must not hijack them.
const ARROW_OWNING_ROLES = new Set([
  'textbox', 'searchbox', 'combobox', 'listbox', 'option', 'slider', 'spinbutton',
  'menu', 'menubar', 'menuitem', 'menuitemcheckbox', 'menuitemradio',
  'radio', 'radiogroup', 'tab', 'tablist', 'tree', 'treeitem', 'grid', 'gridcell',
]);

function targetOwnsArrowKeys(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="dialog"], [role="alertdialog"]')) return true;
  for (let el: HTMLElement | null = target; el; el = el.parentElement) {
    const role = el.getAttribute('role');
    if (role && ARROW_OWNING_ROLES.has(role)) return true;
  }
  return false;
}

const FacilityEditor = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  const { isActive, currentStepData, markStepComplete, nextStep } = useTutorial();

  const [entities, setEntities] = useState<FacilityEntity[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showParkingDialog, setShowParkingDialog] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  // #16/#17: optional camera configuration lives beside the layout, not in it.
  const [mode, setMode] = useState<'layout' | 'cameras'>('layout');

  const viewportRef = useRef<{ fitToContent: (b: Bounds) => void; resetZoom: () => void }>(null);

  // ─── Load bays ──────────────────────────────────────
  const { data: bayRows, isLoading: baysLoading } = useQuery({
    queryKey: ['shop-bays-facility', shopId],
    queryFn: async () => {
      if (!shopId) return [];
      const { data, error } = await supabase
        .from('shop_bays')
        .select('*')
        .eq('shop_id', shopId)
        .order('bay_number', { ascending: true });
      if (error) throw error;
      return data as ShopBayRow[];
    },
    enabled: !!shopId,
  });

  // ─── Load parking lot spots ─────────────────────────
  // Standalone spots (e.g. onboarding parking) exist only here; spots that
  // mirror a shop_bays parking row are deduped by id in mergeFacilityInventory.
  const { data: spotRows, isLoading: spotsLoading } = useQuery({
    queryKey: ['parking-lot-spots-facility', shopId],
    queryFn: async () => {
      if (!shopId) return [];
      const { data, error } = await supabase
        .from('parking_lot_spots')
        .select('*')
        .eq('shop_id', shopId)
        .eq('is_active', true)
        .order('order_index', { ascending: true });
      if (error) throw error;
      return data as ParkingLotSpotRow[];
    },
    enabled: !!shopId,
  });
  const isLoading = baysLoading || spotsLoading;

  // ─── Load technicians ──────────────────────────────
  const { data: technicians } = useQuery({
    queryKey: ['technicians-facility', shopId],
    queryFn: async () => {
      if (!shopId) return [];
      const { data, error } = await supabase
        .from('technicians')
        .select('id, name')
        .eq('shop_id', shopId)
        .eq('employment_status', 'Active')
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
    enabled: !!shopId,
  });

  useEffect(() => {
    if (bayRows && spotRows) {
      if (bayRows.length === 0) {
        setShowWizard(true);
        // Keep any standalone spots visible while the wizard runs.
        setEntities(mergeFacilityInventory([], spotRows).entities);
      } else {
        // Strip any stale ` #<hex>` collision suffixes and renumber
        // in-memory parking duplicates that pre-date the auto-numbering
        // fix. If anything changed, mark the editor dirty so saving
        // propagates the clean names through to `shop_bays.name` and
        // (via the mirror trigger) to `parking_lot_spots.label`.
        // Only shop_bay entities are normalized — standalone spot labels
        // (e.g. onboarding's `001`) are never rewritten.
        const { entities: normalized, changed } = mergeFacilityInventory(bayRows, spotRows);
        setEntities(normalized);
        if (changed > 0) {
          setIsDirty(true);
          toast({
            title: 'Parking labels normalized',
            description:
              `Renumbered ${changed} duplicate parking spot${changed === 1 ? '' : 's'}. ` +
              `Click Save to persist the clean labels in Birdseye.`,
          });
        }
      }
    }
  }, [bayRows, spotRows, toast]);

  // ─── Save ───────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!shopId) return;

      // Delete removed entities
      if (deletedIds.length > 0) {
        const { error } = await supabase
          .from('shop_bays')
          .delete()
          .in('id', deletedIds);
        if (error) throw error;
      }

      // Standalone parking spots: geometry-only UPDATE on parking_lot_spots
      // by their existing id. Never inserted into shop_bays, never routed
      // through replace_parking_lot_spots (which re-creates ids).
      for (const { id, patch } of planStandaloneSpotUpdates(entities, spotRows ?? [])) {
        const { error } = await supabase
          .from('parking_lot_spots')
          .update(patch)
          .eq('id', id)
          .eq('shop_id', shopId);
        if (error) throw error;
      }

      // Upsert all shop_bays-owned entities
      for (const entity of entities) {
        if (isStandaloneParkingSpot(entity)) continue;
        const payload = mapFacilityEntityToShopBayUpdate(entity);
        // Check if entity already exists in DB
        const isNew = !bayRows?.find(b => b.id === entity.id);
        if (isNew) {
          const { error } = await supabase.from('shop_bays').insert({
            ...payload,
            shop_id: shopId,
            name: entity.label,
            bay_type: payload.bay_type ?? 'general',
          });
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('shop_bays')
            .update(payload)
            .eq('id', entity.id);
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      setIsDirty(false);
      setDeletedIds([]);
      queryClient.invalidateQueries({ queryKey: ['shop-bays-facility', shopId] });
      queryClient.invalidateQueries({ queryKey: ['parking-lot-spots-facility', shopId] });
      toast({ title: 'Layout saved' });

      // Auto-advance tutorial when layout is saved
      if (isActive && currentStepData?.id === 'save-layout') {
        markStepComplete('save-layout').catch(err => console.warn('[FacilityEditor] tutorial step complete (save-layout) failed:', err));
        nextStep();
      }
      navigate('/birdseye');
    },
    onError: (err: Error) => {
      // Stage 0D — e.g. removing a parking bay that holds a vehicle: refused.
      toast({ title: 'Save failed', description: layoutSaveRefusalMessage(err.message), variant: 'destructive' });
    },
  });

  // ─── Entity operations ──────────────────────────────
  const markDirty = useCallback(() => setIsDirty(true), []);

  const handleMove = useCallback((id: string, x: number, y: number, soloMove?: boolean) => {
    setEntities(prev => {
      const target = prev.find(e => e.id === id);
      if (!target) return prev;
      const dx = x - target.x;
      const dy = y - target.y;
      // Default: a click-drag on any group member moves the whole row (so
      // repositioning a freshly-generated parking row is one gesture).
      // Shift/Alt-drag (soloMove=true, latched in BayObject.tsx) overrides
      // this so the user can close gaps between resized children manually.
      if (target.groupId && !soloMove) {
        return prev.map(e =>
          e.groupId === target.groupId ? { ...e, x: e.x + dx, y: e.y + dy } : e
        );
      }
      return prev.map(e => e.id === id ? { ...e, x, y } : e);
    });
    markDirty();
  }, [markDirty]);

  // Arrow keys nudge the selected entity one grid space through handleMove,
  // so group vs. solo movement and dirty state match drag exactly
  // (Shift/Alt = solo, as latched in BayObject.tsx). Nothing is persisted
  // until the existing Save button is clicked.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const step = ARROW_KEY_STEP[e.key];
      if (mode !== 'layout' || !step || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
      const selected = selectedId ? entities.find(ent => ent.id === selectedId) : undefined;
      if (!selected) return;
      if (targetOwnsArrowKeys(e.target) || targetOwnsArrowKeys(document.activeElement)) return;
      e.preventDefault();
      handleMove(selected.id, selected.x + step[0], selected.y + step[1], e.shiftKey || e.altKey);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mode, selectedId, entities, handleMove]);

  // Detach the selected entity (and optionally all its siblings) from its
  // group. The group association is purely in-memory — it doesn't survive
  // a Save/reload — but while editing, it controls the group-move behavior
  // above. Ungrouping is the escape hatch when the user wants the spots
  // to live as independent objects from here on.
  const handleUngroup = useCallback((id: string, scope: 'self' | 'group' = 'group') => {
    setEntities(prev => {
      const target = prev.find(e => e.id === id);
      if (!target || !target.groupId) return prev;
      if (scope === 'self') {
        return prev.map(e => e.id === id ? { ...e, groupId: null } : e);
      }
      return prev.map(e => e.groupId === target.groupId ? { ...e, groupId: null } : e);
    });
    markDirty();
  }, [markDirty]);

  const handleResize = useCallback((id: string, width: number, height: number) => {
    setEntities(prev => prev.map(e => e.id === id ? { ...e, width, height } : e));
    markDirty();
  }, [markDirty]);

  const handleUpdate = useCallback((id: string, updates: Partial<FacilityEntity>) => {
    setEntities(prev => prev.map(e => {
      if (e.id !== id) return e;
      // Standalone spots only persist geometry in this slice; drop any
      // other field so nothing unsaveable (label, type, tech…) changes.
      return { ...e, ...(isStandaloneParkingSpot(e) ? restrictStandaloneSpotUpdates(updates) : updates) };
    }));
    markDirty();
  }, [markDirty]);

  const handleDelete = useCallback((id: string) => {
    // Standalone parking spots are not deletable in this slice, and must
    // never reach the shop_bays delete path below.
    const target = entities.find(e => e.id === id);
    if (!target || isStandaloneParkingSpot(target)) return;
    setEntities(prev => prev.filter(e => e.id !== id));
    setDeletedIds(prev => [...prev, id]);
    setSelectedId(null);
    markDirty();
  }, [entities, markDirty]);

  const handleAddEntity = useCallback((type: FacilityEntityType) => {
    if (!shopId) return;
    const config = ENTITY_RENDER_CONFIG[type];
    // Type-scoped numbering: scan only entities of the same type so a new
    // Parking spot picks up from the highest existing `Parking <N>` rather
    // than from `entities.length + 1` (which previously counted every bay
    // and produced wrong-but-unrelated numbers like "Parking 11" when
    // there were 10 work bays + 0 parking).
    const num = nextLabelNumber(entities, type);
    const newEntity: FacilityEntity = {
      id: crypto.randomUUID(),
      // New editor entities — parking included — still persist via
      // shop_bays (+ the DB mirror) in this slice.
      storageSource: 'shop_bay',
      entityType: type,
      label: `${config.displayLabel} ${num}`,
      x: 100,
      y: 100,
      width: config.defaultWidth,
      height: config.defaultHeight,
      isOutdoor: type === 'parking_space',
      isActive: true,
      isLockedOut: false,
      lockoutReason: null,
      isCommunity: false,
      assignedTechId: null,
      teamId: null,
      shopId,
      bayNumber: num,
      dbBayType: type === 'parking_space' ? 'general' : (
        type === 'lift_2post' ? '2-post' :
        type === 'lift_4post' ? '4-post' :
        type === 'lift_scissor' ? 'scissor' :
        type === 'alignment' ? 'alignment' :
        type === 'adas' ? 'adas' :
        type === 'alignment_adas' ? 'alignment-adas' :
        type === 'wash' ? 'wash' :
        'flat'
      ),
      dbBayCategory: type === 'parking_space' ? 'parking' : 'general',
    };
    setEntities(prev => [...prev, newEntity]);
    setSelectedId(newEntity.id);
    markDirty();

    // Auto-advance tutorial when first bay is added
    if (isActive && currentStepData?.id === 'create-bay') {
      markStepComplete('create-bay').catch(err => console.warn('[FacilityEditor] tutorial step complete (create-bay) failed:', err));
      nextStep();
    }
  }, [shopId, entities, markDirty, isActive, currentStepData, markStepComplete, nextStep]);

  const handleGenerateParkingRow = useCallback((count: number, orientation: 'horizontal' | 'vertical', gap: number) => {
    if (!shopId) return;
    // Start numbering after the highest existing parking spot so a new
    // row of 7 in a shop with `Parking 1`..`Parking 4` produces
    // `Parking 5`..`Parking 11` (no label collisions, no trigger-side
    // `#<hex>` fallback in `parking_lot_spots`).
    const startNumber = nextLabelNumber(entities, 'parking_space');
    const row = generateParkingRow(count, orientation, 100, 500, shopId, startNumber, gap);
    setEntities(prev => [...prev, ...row]);
    markDirty();
  }, [shopId, markDirty, entities]);

  const handleWizardComplete = useCallback((config: {
    standardBays: number;
    specialty: { alignment: boolean; adas: boolean; combo: boolean };
    layout: 'straight' | 'two_rows' | 'u_shape';
    frontParking: number;
    rearParking: number;
  }) => {
    if (!shopId) return;
    const generated = generateWizardLayout(shopId, config);
    // The wizard replaces editor-owned entities only; standalone parking
    // spots are kept (they are not created or deleted by the editor).
    setEntities(prev => [...prev.filter(isStandaloneParkingSpot), ...generated]);
    setShowWizard(false);
    setIsDirty(true);

    // Fit to content after layout
    setTimeout(() => {
      if (viewportRef.current && generated.length > 0) {
        viewportRef.current.fitToContent({
          minX: Math.min(...generated.map(e => e.x)),
          minY: Math.min(...generated.map(e => e.y)),
          maxX: Math.max(...generated.map(e => e.x + e.width)),
          maxY: Math.max(...generated.map(e => e.y + e.height)),
        });
      }
    }, 100);
  }, [shopId]);

  const selectedEntity = entities.find(e => e.id === selectedId) ?? null;

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top bar */}
      <div className="h-12 border-b border-border flex items-center px-3 gap-3 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => navigate('/shop-floor')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-sm font-semibold flex-1 truncate">Facility Editor</h1>
        <div role="tablist" aria-label="Facility Editor section" className="flex rounded-md border p-0.5">
          {(['layout', 'cameras'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              data-testid={`facility-mode-${m}`}
              onClick={() => setMode(m)}
              className={`min-h-9 rounded px-3 text-sm ${mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
            >
              {m === 'layout' ? 'Layout' : 'Cameras'}
            </button>
          ))}
        </div>
        {mode === 'layout' && (
        <Button
          size="sm"
          disabled={!isDirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          data-testid="facility-save-button"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save
        </Button>
        )}
      </div>

      {mode === 'cameras' && shopId && (
        <div className="flex-1 min-h-0">
          <CameraManager shopId={shopId} />
        </div>
      )}

      {/* Main area */}
      <div className={mode === 'layout' ? 'flex flex-1 min-h-0' : 'hidden'}>
        {/* Left sidebar: Tools + Inspector */}
        <div className="flex flex-col w-60 border-r border-border shrink-0 overflow-hidden">
          <ToolPalette
            onAddEntity={handleAddEntity}
            onAddParkingRow={() => setShowParkingDialog(true)}
          />
          <div className="border-t border-border flex-1 min-h-0 overflow-auto" data-testid="facility-inspector-panel">
            <InspectorPanel
              entity={selectedEntity}
              technicians={technicians ?? []}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onUngroup={handleUngroup}
              groupSiblingCount={
                selectedEntity?.groupId
                  ? entities.filter(e => e.groupId === selectedEntity.groupId).length
                  : 0
              }
            />
          </div>
        </div>

        {/* Viewport */}
        <div className="flex-1 min-w-0">
          <Viewport ref={viewportRef} entities={entities}>
            <FacilityCanvas
              entities={entities}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onDeselect={() => setSelectedId(null)}
              onMove={handleMove}
              onResize={handleResize}
            />
          </Viewport>
        </div>
      </div>

      {/* Dialogs */}
      <ParkingRowDialog
        open={showParkingDialog}
        onOpenChange={setShowParkingDialog}
        onGenerate={handleGenerateParkingRow}
      />
      <SetupWizard
        open={showWizard}
        onComplete={handleWizardComplete}
      />

      <Coachmark stepId="create-bay" targetSelector="facility-tool-palette" arrowPosition="left" arrowOffset={60} />
      <Coachmark stepId="edit-bay" targetSelector="facility-inspector-panel" arrowPosition="left" arrowOffset={60} />
      <Coachmark stepId="save-layout" targetSelector="facility-save-button" arrowPosition="top" arrowOffset={40} />
    </div>
  );
};

export default FacilityEditor;
